// =============================================================================
// features/credit-analyses/components/DocumentChecklistHeader.tsx — Cabeçalho
// de checklist de documentos ("Documentação 4/6") — F46-S10.
//
// Consome useDocumentChecklist(flow, contextId) e renderiza:
//   - Resumo "Documentação X/Y" (summary.approved/summary.required).
//   - Itens obrigatórios pendentes (missing/expired/rejected/pending_review)
//     em destaque; aprovados recolhidos atrás de um toggle.
//   - DocumentReviewActions por item em pending_review, só com
//     `documents:review`.
//   - "Pedir documentos ao cliente": monta o texto da pendência e abre um
//     seletor de respostas rápidas (features/quick-replies, F28) para o
//     analista compor a mensagem — NUNCA envia. O envio ao cidadão é sempre
//     ato humano, feito manualmente no WhatsApp/live chat.
//
// Documento vencido é PENDÊNCIA, não aprovado — `expired` nunca herda o
// estilo de `approved` aqui (é o erro mais fácil de cometer nesta tela e o
// que mais custa: analista fecha crédito achando documentação completa).
// =============================================================================

import type {
  ChecklistResponse,
  DocumentChecklistItem,
  DocumentChecklistState,
} from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge, type BadgeVariant } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import { useQuickReplies } from '../../quick-replies';
import type { ChecklistFlow } from '../hooks/useDocumentChecklist';
import { useDocumentChecklist } from '../hooks/useDocumentChecklist';

import { DocumentReviewActions } from './DocumentReviewActions';

// ─── Helpers puros (testados em __tests__/document-checklist.test.tsx) ─────

const STATE_LABEL: Record<DocumentChecklistState, string> = {
  missing: 'Faltando',
  pending_review: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  expired: 'Vencido',
};

const STATE_BADGE_VARIANT: Record<DocumentChecklistState, BadgeVariant> = {
  missing: 'warning',
  pending_review: 'info',
  approved: 'success',
  rejected: 'danger',
  expired: 'danger',
};

/**
 * Decide se as ações de conferência (aprovar/rejeitar) aparecem para um
 * item — só `pending_review` é revisável (backend responde 409 fora disso,
 * F46-S06) E só com `documents:review` (permissão DELIBERADAMENTE separada
 * de `documents:read`, mesma nota de F46-S08). Papel `agente` (sem
 * `documents:review`) nunca vê o botão, mesmo com o item pronto para
 * revisão — teste negativo em __tests__/document-checklist.test.tsx.
 */
export function shouldShowReviewActions(item: DocumentChecklistItem, canReview: boolean): boolean {
  return canReview && item.state === 'pending_review' && item.documentId !== undefined;
}

/** Estados que exigem ação do cidadão (nunca `approved`, mesmo com `expiresAt` no passado —
 * o backend já resolve isso: doc aprovado vencido chega aqui como `expired`, nunca `approved`). */
export function needsCitizenAction(state: DocumentChecklistState): boolean {
  return state === 'missing' || state === 'expired' || state === 'rejected';
}

/** Ordena pendências primeiro (missing/expired/rejected), depois em análise, aprovados por
 * último — é o que o analista precisa ver sem rolar a tela. */
export function sortChecklistItems(
  items: readonly DocumentChecklistItem[],
): DocumentChecklistItem[] {
  const priority: Record<DocumentChecklistState, number> = {
    missing: 0,
    expired: 0,
    rejected: 0,
    pending_review: 1,
    approved: 2,
  };
  return [...items].sort((a, b) => priority[a.state] - priority[b.state]);
}

/**
 * Monta o texto de "pedir documentos ao cliente" a partir das pendências
 * reais do checklist. Puro — testável sem rede. Documento `rejected` inclui
 * o rótulo do tipo, nunca o `rejectionReason` bruto (não está no
 * DocumentChecklistItem — o motivo fica só na conferência, não vaza aqui).
 */
export function buildPendingDocumentsMessage(items: readonly DocumentChecklistItem[]): string {
  const pending = items.filter((item) => needsCitizenAction(item.state));
  if (pending.length === 0) return '';

  const lines = pending.map((item) => {
    const suffix =
      item.state === 'expired'
        ? ' (vencido — envie uma versão atualizada)'
        : item.state === 'rejected'
          ? ' (foi rejeitado — envie uma nova versão)'
          : '';
    return `- ${item.label}${suffix}`;
  });

  return [
    'Olá! Para seguir com a sua análise de crédito, precisamos que você envie os seguintes documentos:',
    '',
    ...lines,
    '',
    'Pode responder esta mensagem com as fotos ou PDFs. Obrigado!',
  ].join('\n');
}

// ─── Badge de estado ─────────────────────────────────────────────────────────

function ChecklistStateBadge({ state }: { state: DocumentChecklistState }): React.JSX.Element {
  return <Badge variant={STATE_BADGE_VARIANT[state]}>{STATE_LABEL[state]}</Badge>;
}

// ─── Linha de item ───────────────────────────────────────────────────────────

function ChecklistItemRow({
  item,
  canReview,
  flow,
  contextId,
}: {
  item: DocumentChecklistItem;
  canReview: boolean;
  flow: ChecklistFlow;
  contextId: string;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3"
      style={{ borderBottom: '1px solid var(--border-subtle)' }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <ChecklistStateBadge state={item.state} />
        <span className="font-sans text-sm font-medium text-ink truncate">{item.label}</span>
      </div>

      {shouldShowReviewActions(item, canReview) && item.documentId !== undefined && (
        <DocumentReviewActions
          documentId={item.documentId}
          documentLabel={item.label}
          flow={flow}
          contextId={contextId}
          canReview={canReview}
        />
      )}
    </div>
  );
}

// ─── Drawer "Pedir documentos ao cliente" ───────────────────────────────────

function RequestDocumentsDrawer({
  initialMessage,
  onClose,
}: {
  initialMessage: string;
  onClose: () => void;
}): React.JSX.Element {
  const { toast } = useToast();
  const [message, setMessage] = React.useState(initialMessage);
  const [search, setSearch] = React.useState('');

  const { data: quickReplies, isLoading: isLoadingQuickReplies } = useQuickReplies({
    visibility: 'organization',
    isActive: true,
    limit: 50,
  });

  const filtered = React.useMemo(() => {
    const items = quickReplies?.data ?? [];
    const normalized = search.trim().toLowerCase();
    if (normalized.length === 0) return items;
    return items.filter((item) =>
      `${item.title} ${item.body ?? ''}`.toLowerCase().includes(normalized),
    );
  }, [quickReplies, search]);

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(message);
      toast('Mensagem copiada. Envie manualmente pelo WhatsApp ou live chat.', 'success');
    } catch {
      toast('Não foi possível copiar automaticamente — selecione e copie o texto.', 'danger');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-documents-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-2xl rounded-md flex flex-col gap-5 p-6 max-h-[85vh] overflow-y-auto"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
      >
        <div className="flex flex-col gap-1.5">
          <h2
            id="request-documents-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
          >
            Pedir documentos ao cliente
          </h2>
          <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
            Este texto <strong>não é enviado automaticamente</strong>. Copie e envie manualmente
            pelo WhatsApp ou pelo live chat do lead — o contato com o cidadão é sempre ação humana.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="request-documents-message"
            className="font-sans text-xs font-semibold text-ink-2 uppercase tracking-[0.1em]"
          >
            Mensagem
          </label>
          <textarea
            id="request-documents-message"
            rows={7}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full font-sans text-sm font-medium text-ink bg-surface-1 rounded-sm px-[14px] py-[11px] border border-border-strong shadow-[inset_0_1px_2px_var(--border-inner-dark)] transition-[border-color,box-shadow] duration-fast ease placeholder:text-ink-4 hover:border-ink-3 focus:outline-none focus:border-azul focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)] resize-y min-h-[160px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="request-documents-quick-reply-search"
            className="font-sans text-xs font-semibold text-ink-2 uppercase tracking-[0.1em]"
          >
            Ou use uma resposta rápida
          </label>
          <input
            id="request-documents-quick-reply-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título ou texto..."
            className="w-full font-sans text-sm text-ink bg-surface-1 rounded-sm px-[14px] py-[9px] border border-border-strong shadow-[inset_0_1px_2px_var(--border-inner-dark)] placeholder:text-ink-4 focus:outline-none focus:border-azul focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]"
          />

          <div
            className="flex flex-col rounded-sm border border-border-subtle max-h-[180px] overflow-y-auto"
            role="listbox"
            aria-label="Respostas rápidas disponíveis"
          >
            {isLoadingQuickReplies && (
              <p className="font-sans text-xs text-ink-3 px-3 py-3">Carregando…</p>
            )}
            {!isLoadingQuickReplies && filtered.length === 0 && (
              <p className="font-sans text-xs text-ink-3 px-3 py-3">
                Nenhuma resposta rápida encontrada.
              </p>
            )}
            {filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => setMessage(item.body ?? '')}
                className="text-left px-3 py-2 font-sans text-xs text-ink hover:bg-surface-hover transition-colors duration-fast ease border-b border-border-subtle last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30"
              >
                <span className="font-semibold">{item.title}</span>
                {item.body !== null && (
                  <span className="block text-ink-3 truncate">{item.body}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 justify-center">
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleCopy()}
            disabled={message.trim().length === 0}
            className="flex-1 justify-center"
          >
            Copiar mensagem
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton / erro ─────────────────────────────────────────────────────────

function HeaderSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-4 animate-pulse flex flex-col gap-2"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="h-4 w-40 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
      <div className="h-3 w-full rounded-xs" style={{ background: 'var(--surface-muted)' }} />
    </div>
  );
}

function HeaderError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      className="rounded-md p-4 flex items-center justify-between gap-3"
      style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)' }}
    >
      <p className="font-sans text-sm font-semibold" style={{ color: 'var(--danger)' }}>
        Não foi possível carregar o checklist de documentos.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export interface DocumentChecklistHeaderProps {
  flow: ChecklistFlow;
  contextId: string;
}

export function computeChecklistSummaryLabel(summary: ChecklistResponse['summary']): string {
  return `Documentação ${summary.approved}/${summary.required}`;
}

export function DocumentChecklistHeader({
  flow,
  contextId,
}: DocumentChecklistHeaderProps): React.JSX.Element | null {
  const { data, isLoading, isError, refetch, canRead, canReview, isGateLoading } =
    useDocumentChecklist(flow, contextId);
  const [showApproved, setShowApproved] = React.useState(false);
  const [requestOpen, setRequestOpen] = React.useState(false);

  if (isGateLoading) return <HeaderSkeleton />;
  if (!canRead) return null;
  if (isLoading) return <HeaderSkeleton />;
  if (isError) return <HeaderError onRetry={refetch} />;
  if (!data || data.required.length === 0) return null;

  const sorted = sortChecklistItems(data.required);
  const pendingItems = sorted.filter((item) => item.state !== 'approved');
  const approvedItems = sorted.filter((item) => item.state === 'approved');
  const hasCitizenPending = data.required.some((item) => needsCitizenAction(item.state));

  return (
    <div
      className="rounded-md border border-border bg-surface-1 overflow-hidden"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <h3
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
          >
            {computeChecklistSummaryLabel(data.summary)}
          </h3>
          <Badge variant={data.summary.complete ? 'success' : 'warning'}>
            {data.summary.complete ? 'Completo' : 'Pendente'}
          </Badge>
        </div>

        {hasCitizenPending && (
          <Button variant="outline" size="sm" onClick={() => setRequestOpen(true)}>
            Pedir documentos ao cliente
          </Button>
        )}
      </div>

      <div>
        {pendingItems.map((item) => (
          <ChecklistItemRow
            key={item.documentTypeId}
            item={item}
            canReview={canReview}
            flow={flow}
            contextId={contextId}
          />
        ))}

        {approvedItems.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowApproved((v) => !v)}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 font-sans text-xs font-semibold text-ink-3 uppercase tracking-[0.08em] hover:bg-surface-hover transition-colors duration-fast ease focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20"
              aria-expanded={showApproved}
            >
              <span>
                {approvedItems.length} aprovado{approvedItems.length === 1 ? '' : 's'}
              </span>
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="w-3.5 h-3.5 transition-transform duration-fast ease"
                style={{ transform: showApproved ? 'rotate(180deg)' : 'rotate(0deg)' }}
                aria-hidden="true"
              >
                <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showApproved &&
              approvedItems.map((item) => (
                <ChecklistItemRow
                  key={item.documentTypeId}
                  item={item}
                  canReview={canReview}
                  flow={flow}
                  contextId={contextId}
                />
              ))}
          </>
        )}
      </div>

      {requestOpen && (
        <RequestDocumentsDrawer
          initialMessage={buildPendingDocumentsMessage(data.required)}
          onClose={() => setRequestOpen(false)}
        />
      )}
    </div>
  );
}
